import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import FolderIcon from '@mui/icons-material/Folder'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import useFUBVerification from '../hooks/useFUBVerification'
import useDotloopAuth from '../hooks/useDotloopAuth'
import { getLeadLoops, getDotloopUrl, LeadLoop } from '../services/loopsApi'
import AdminStatusMenu from '../components/AdminStatusMenu'

export default function HomePage() {
  const navigate = useNavigate()
  const fubContext = useFUBVerification()
  const personId = fubContext?.person?.id
  const personName = fubContext?.person
    ? `${fubContext.person.firstName || ''} ${fubContext.person.lastName || ''}`.trim()
    : 'Lead'

  const {
    authStatus,
    loading: authLoading,
    initiateAuth,
    logout,
    isAuthenticated,
    needsReauth,
    validationError,
    dotloopEmail
  } = useDotloopAuth(fubContext?.user?.id)

  const [loops, setLoops] = useState<LeadLoop[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if FUB API key is configured
  const hasFubApiKey = !!process.env.REACT_APP_FUB_API_KEY

  // Fetch loops for this lead
  useEffect(() => {
    async function fetchLoops() {
      if (!personId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const data = await getLeadLoops(personId)
        setLoops(data)
      } catch (err) {
        console.error('Error fetching loops:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch loops')
      } finally {
        setLoading(false)
      }
    }

    fetchLoops()
  }, [personId])

  const handleOpenLoop = (loop: LeadLoop) => {
    const url = loop.loop_url || getDotloopUrl(loop.dotloop_loop_id)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleCreateNew = () => {
    // Preserve query params (context, signature) when navigating
    const currentSearch = window.location.search
    navigate(`/create${currentSearch}`)
  }

  // Loading state for authentication check
  if (authLoading) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Checking DotLoop connection...
          </Typography>
        </Box>
      </Box>
    )
  }

  // Not authenticated - Show connect button
  if (!isAuthenticated) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1000
          }}
        >
          <AdminStatusMenu
            isAuthenticated={isAuthenticated}
            authLoading={authLoading}
            onAuthenticate={initiateAuth}
            onLogout={logout}
            hasFubApiKey={hasFubApiKey}
            dotloopEmail={dotloopEmail}
            validationError={validationError}
            needsReauth={needsReauth}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            px: 3,
            gap: 2
          }}
        >
          {/* Warning message if needs reauth */}
          {needsReauth && (
            <Alert 
              severity="warning" 
              sx={{ 
                maxWidth: 450,
                width: '100%'
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Authentication Required
              </Typography>
              <Typography variant="body2">
                Please connect your DotLoop account to create and manage transaction loops.
              </Typography>
            </Alert>
          )}
          
          <Card
            sx={{
              maxWidth: 400,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 3
            }}
          >
            <CardContent sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Connect to DotLoop
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your DotLoop account to create and manage transactions
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={initiateAuth}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Connect to DotLoop
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Admin Status Menu - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1000
        }}
      >
        <AdminStatusMenu
          isAuthenticated={isAuthenticated}
          authLoading={authLoading}
          onAuthenticate={initiateAuth}
          onLogout={logout}
          hasFubApiKey={hasFubApiKey}
          dotloopEmail={dotloopEmail}
          validationError={validationError}
          needsReauth={needsReauth}
        />
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 2, pt: 6 }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            DotLoop Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {personName ? `Loops for ${personName}` : 'Manage your DotLoop transactions'}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loops List */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : loops.length > 0 ? (
              <List disablePadding>
                {loops.map((loop, index) => (
                  <ListItem
                    key={loop.id}
                    disablePadding
                    divider={index < loops.length - 1}
                  >
                    <ListItemButton onClick={() => handleOpenLoop(loop)}>
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={loop.loop_name || `Loop ${loop.dotloop_loop_id}`}
                        secondary={
                          <>
                            {loop.transaction_type && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {loop.transaction_type.replace(/_/g, ' ')}
                              </Typography>
                            )}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              Created: {new Date(loop.created_at).toLocaleDateString()}
                            </Typography>
                          </>
                        }
                      />
                      <OpenInNewIcon fontSize="small" color="action" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No loops yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first DotLoop transaction to get started
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Create New Loop Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem'
          }}
        >
          Create New Loop
        </Button>
      </Box>
    </Box>
  )
}
